<template>
  <div class="ma-4">
    <v-snackbar
      v-model="msg"
      top
      :color="snackbarColor"
      :timeout="3000"
      elevation="4"
      class="rounded-lg"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">{{
          snackbarColor === "success" ? "mdi-check-circle" : "mdi-alert"
        }}</v-icon>
        {{ text }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="msg = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-card class="rounded-lg">
      <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg px-3">
        <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
          <v-icon>navigate_before</v-icon>
        </v-btn>

        <span class="text-h6 font-weight-medium grey--text text--darken-3">
          Attendance Report
        </span>

        <v-spacer></v-spacer>
        
        <report-list-actions
          :reportOptions="reportOptions"
          :downloadFilename="reportListFilename"
          class="mr-2"
        />

      </v-app-bar>

      <v-divider></v-divider>

      <v-card-text class="pa-6 grey lighten-5">
        <v-row dense>
          <v-col cols="12" md="2">
            <v-menu
              ref="calendarMenu"
              v-model="calendarMenu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="reportFilters.startDate"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :rules='requiredField'
                  clearable
                  @change="getAttendance()"
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="reportFilters.startDate"
                no-title
                scrollable
                :rules='requiredField'
                class="rounded-lg"
              >
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="primary"
                  @click="calendarMenu = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="$refs.calendarMenu.save(date)"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-menu
              ref="calendar2Menu"
              v-model="calendar2Menu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="reportFilters.endDate"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :rules='requiredField'
                  clearable
                  @change="getAttendance()"
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="reportFilters.endDate"
                no-title
                scrollable
                :rules='requiredField'
                class="rounded-lg"
              >
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="primary"
                  @click="calendar2Menu = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="$refs.calendar2Menu.save(date)"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              label="Client Status"
              :items="clientStatuses"
              v-model="reportFilters.clientStatus"
              clearable
              @change="getAttendance()"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              label="Select a Client"
              :items="filteredClients"
              item-text="Name"
              item-value="._id"
              v-model="reportFilters.ClientID"
              clearable
              @change="getAttendance()"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              label="Select a Service Code"
              :items="serviceCodes"
              v-model="reportFilters.ServiceCode"
              :disabled="isServiceCodeDisabled"
              clearable
              @change="getAttendance()"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-select>
          </v-col>

          <v-col cols="12" md="1">
            <v-btn
              color="primary"
              block
              height="40"
              class="rounded-lg"
              :loading="isLoading"
              elevation="1"
              @click="getAttendance()"
            >
              <v-icon left>mdi-magnify</v-icon>
              Search
            </v-btn>
          </v-col>

          <v-col cols="12" md="1">
            <v-btn
              color="success"
              block
              height="40"
              class="rounded-lg"
              elevation="1"
              @click="downloadAttendance()"
            >
              <v-icon left>mdi-download</v-icon>
              Download
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-card-text class="pa-6">
        <v-data-table
          :page="page"
          :pageCount="pageCount"
          :server-items-length="itemCount"
          :options.sync="options"
          :headers="headers"
          :items="attendance"
          sort-by="Date"
          sort-desc
          class="elevation-1 rounded-lg"
          :loading="isLoading"
          loading-text="Loading Attendance"
          :footer-props="{
            'items-per-page-options': [5, 10, 20, 50],
            showFirstLastPage: true,
            'items-per-page-text': 'Rows per page:',
            class: 'rounded-lg',
          }"
        >
          <template v-slot:item.select="{ item }">
            <v-btn
              x-small
              color="primary"
              @click="viewAttendance(item)"
              class="px-3"
            >
              <v-icon left x-small>mdi-eye</v-icon>
              View
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

  </div>
</template>
<script>
import axios from 'axios'
import {STATUS_ARRAY} from '@/enums/UserStatusEnum';
import download from "downloadjs";
import {mapActions} from "vuex";
import moment from 'moment';
import ReportFilters from '@/mixins/reportFilters.js';
import DateTimeUtil from "../../../utils/date-time.util";
import ReportListActions from '@/components/Report/ReportListActions.vue';
import {SORT_ORDER} from '@/constants/MongoDBConstants.js';


// import moment from 'moment'
// import Foot from '../components/Footer'

const url = require("../../client_config/config").url;
const HttpService = require("../../service/http.service");

export default {
  mixins: [
    ReportFilters,
  ],
  components: {
    ReportListActions
  },
  data: () => ({
    headers: [
      {text: 'Start Date', align: 'start', value: 'Date', sortable: true},
      {text: "Client", value: "ClientInfo.Name"},
      {text: "Service Code", value: "Code"},
      {text: "Service Unit", value: "ServiceUnit"},
      {text: "Units", value: "Units"},
      {text: "Start Time", value: "TimeStart"},
      {text: "End Time", value: "TimeEnd"},
      {text: "Signature", value: "Signature"},
      {text: "", value: "select"},
      // { text: "Actions", value: "actions", sortable: false }
    ],
    date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
    // page: 'Incidents',
    clients: [],
    clientIds: [],
    serviceCodes: [],
    attendance: [],
    calendarMenu: false,
    calendar2Menu: false,
    report: '',
    clientName: '',
    isLoading: false,
    requiredField: [
      v => !!v || 'This is required',
    ],
    isServiceCodeDisabled: false,
    userState: localStorage.getItem('userState'),
    dialog: false,
    editAttendance: false,
    newAttendance: false,
    msg: false,
    snackbarColor: "success",
    search: "",
    text: "",
    codes: [],
    users: [],
    userID: localStorage.getItem('userID'),
    staff: localStorage.getItem('Name'),
    tempData: [],
    titleForm: "New Attendance Record",
    attendanceID: "",
    newName: "",
    newTitle: "",
    newCode: "",
    newDate: "",
    newSignature: "",
    Rate: "",
    signed: false,
    checkSignature: false,
    newUnits: "",
    newServiceUnit: "",
    requiredRules: [
      v => !!v || 'This is required',
    ],
    timeStartPicker: false,
    timeStart: null,
    timeEndPicker: false,
    timeEnd: null,
    timeEndAdjust: null,
    requiredTime: false,
    newServiceCodeDialog: false,
    newServiceCodeAdd: "",
    maskDate: [/\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/],
    maskTimeStart: [/[1]/, /\d/, ':', /[0-5]/, /\d/, ' ', /[A,P]/, 'M'],
    maskTimeEnd: [/[1]/, /\d/, ':', /[0-5]/, /\d/, ' ', /[A,P]/, 'M'],
    showStartTimePicker: false,
    showEndTimePicker: false,
    timeStartDisplay: '',
    timeEndDisplay: '',
    menu: false,
    clientState: "",
    page: 1,
    pageCount: 1,
    itemCount: 0,
    options: {},
    clientStatuses: STATUS_ARRAY,
    readonly: true,
    disabled: true,
    client: false,
    createAttendance: false,
    times: [
      {Time: "6:00am", Value: "6:00am"}, {Time: "6:15am", Value: "6:15am"}, {
        Time: "6:30am",
        Value: "6:30am"
      }, {Time: "6:45am", Value: "6:45am"},
      {Time: "7:00am", Value: "7:00am"}, {Time: "7:15am", Value: "7:15am"}, {
        Time: "7:30am",
        Value: "7:30am"
      }, {Time: "7:45am", Value: "7:45am"},
      {Time: "8:00am", Value: "8:00am"}, {Time: "8:15am", Value: "8:15am"}, {
        Time: "8:30am",
        Value: "8:30am"
      }, {Time: "8:45am", Value: "8:45am"},
      {Time: "9:00am", Value: "9:00am"}, {Time: "9:15am", Value: "9:15am"}, {
        Time: "9:30am",
        Value: "9:30am"
      }, {Time: "9:45am", Value: "9:45am"},
      {Time: "10:00am", Value: "10:00am"}, {Time: "10:15am", Value: "10:15am"}, {
        Time: "10:30am",
        Value: "10:30am"
      }, {Time: "10:45am", Value: "10:45am"},
      {Time: "11:00am", Value: "11:00am"}, {Time: "11:15am", Value: "11:15am"}, {
        Time: "11:30am",
        Value: "11:30am"
      }, {Time: "11:45am", Value: "11:45am"},
      {Time: "12:00pm", Value: "12:00pm"}, {Time: "12:15pm", Value: "12:15pm"}, {
        Time: "12:30pm",
        Value: "12:30pm"
      }, {Time: "12:45pm", Value: "12:45pm"},
      {Time: "1:00pm", Value: "13:00pm"}, {Time: "1:15pm", Value: "13:15pm"}, {
        Time: "1:30pm",
        Value: "13:30pm"
      }, {Time: "1:45pm", Value: "13:45pm"},
      {Time: "2:00pm", Value: "14:00pm"}, {Time: "2:15pm", Value: "14:15pm"}, {
        Time: "2:30pm",
        Value: "14:30pm"
      }, {Time: "2:45pm", Value: "14:45pm"},
      {Time: "3:00pm", Value: "15:00pm"}, {Time: "3:15pm", Value: "15:15pm"}, {
        Time: "3:30pm",
        Value: "15:30pm"
      }, {Time: "3:45pm", Value: "15:45pm"},
      {Time: "4:00pm", Value: "16:00pm"}, {Time: "4:15pm", Value: "16:15pm"}, {
        Time: "4:30pm",
        Value: "16:30pm"
      }, {Time: "4:45pm", Value: "16:45pm"},
      {Time: "5:00pm", Value: "17:00pm"}, {Time: "5:15pm", Value: "17:15pm"}, {
        Time: "5:30pm",
        Value: "17:30pm"
      }, {Time: "5:45pm", Value: "17:45pm"},
      {Time: "6:00pm", Value: "18:00pm"}, {Time: "6:15pm", Value: "18:15pm"}, {
        Time: "6:30pm",
        Value: "18:30pm"
      }, {Time: "6:45pm", Value: "18:45pm"},
      {Time: "7:00pm", Value: "19:00pm"}, {Time: "7:15pm", Value: "19:15pm"}, {
        Time: "7:30pm",
        Value: "19:30pm"
      }, {Time: "7:45pm", Value: "19:45pm"},
      {Time: "8:00pm", Value: "20:00pm"}, {Time: "8:15pm", Value: "20:15pm"}, {
        Time: "8:30pm",
        Value: "20:30pm"
      }, {Time: "8:45pm", Value: "20:45pm"},
      {Time: "9:00pm", Value: "21:00pm"}, {Time: "9:15pm", Value: "21:15pm"}, {
        Time: "9:30pm",
        Value: "21:30pm"
      }, {Time: "9:45pm", Value: "21:45pm"},
      {Time: "10:00pm", Value: "22:00pm"}, {Time: "10:15pm", Value: "22:15pm"}, {
        Time: "10:30pm",
        Value: "22:30pm"
      }, {Time: "10:45pm", Value: "22:45pm"},
      {Time: "11:00pm", Value: "23:00pm"}, {Time: "11:15pm", Value: "23:15pm"}, {
        Time: "11:30pm",
        Value: "23:30pm"
      }, {Time: "11:45pm", Value: "23:45pm"},

    ],
    reportFilters: {
      startDate: '',
      endDate: '',
      clientStatus: '',
      ClientID: '',
      ServiceCode: '',
    },
    reportFilterName: 'ReportFilters_AttendanceReport'
  }),

  computed: {
    filteredClients() {
      const clientStatus = this.reportFilters.clientStatus;
      if (!(clientStatus == '' || clientStatus == null)) {
        return this.clients.filter((client) => client.Status == clientStatus);
      }

      return this.clients;
    },
    reportOptions() {
        return { 
            query: this.queryParams,
            endpoints: `attendance/printListAll`,
            dynamic: true
        }
    },
    reportListFilename() {
        return `Attendance Report`;
    },
    queryParams() {      
      let startDate;
      let endDate;


      if (this.reportFilters.startDate) {
        startDate = new Date(this.reportFilters.startDate);
        startDate.setHours(0, 0, 0);
        startDate.toISOString()
      } else {
        startDate =  this.reportFilters.startDate
      }

      if (this.reportFilters.endDate) {
        endDate = new Date(this.reportFilters.endDate);
        endDate.setHours(23, 59, 59);
        endDate.toISOString()
      } else {
        endDate =  this.reportFilters.endDate
      }


      let {page, itemsPerPage, sortBy, sortDesc} = this.options;
      page = page || 1;
      itemsPerPage = itemsPerPage || 10;
      let sortField = sortBy ? sortBy[0] : 'Date';
      const sortDirection = Array.isArray(sortDesc) ? (sortDesc[0] ? SORT_ORDER.DESC:SORT_ORDER.ASC) : SORT_ORDER.DESC;

      return {
          page: page,
          limit: itemsPerPage,
          sortField: sortField,
          sortDirection: sortDirection,
          clientId: this.reportFilters.ClientID,
          serviceCode: this.reportFilters.ServiceCode,
          startDate: startDate,
          endDate: endDate,
          clientStatus: this.reportFilters.clientStatus,
          state: this.userState,
       };
    }
  },

  watch: {
    options: {
      handler() {
        this.getAttendance();
      },
      deep: true
    },

    newCode(newVal) {
      if (newVal && newVal.ServiceTitle) {
        this.newTitle = newVal.ServiceTitle;
      }
    },

    reportFilters: {
      handler(newVal) {
        this.setReportFilters(this.reportFilterName, newVal);
      },
      deep: true
    }
  },

  created() {
    this.getClients();
    this.getServiceCodesFromCode();
    this.getAttendance();

    this.reportFilters = this.getReportFilters(this.reportFilterName) ?? this.reportFilters;
  },

  methods: {

    ...mapActions({
      getJDTAttendanceReportPDF: "dataTracking/getJDTAttendanceReportPDF",
      getClientAttendance: "attendance/getClientAttendance",
    }),

    initialize() {
      this.clients = [];
      this.serviceCodes = [];
    },

    getClients() {
      let endpoint = `${url}/clients`;

      axios.get(endpoint)
        .then((res) => {
          this.clients = res.data.clients || [];
          this.clientIds = this.clients.map(client => client._id);
          this.getAttendance();
        });

    },
    getServiceCodesFromCode() {
      axios.get(`${url}/codes/list`).then(async res => {
        this.serviceCodes = res.data;
      });
    },

    getCodes() {
      axios.get(`${url}/serviceCodes/get/allClientCodes/${this.reportFilters.ClientID}`).then(async res => {
        this.codes = res.data.activeServiceCodes;
      });
    },

    getClientState() {
      axios.get(`${url}/clients/${this.reportFilters.ClientID}`).then(res => {
        this.clientState = res.data.client.State;
      })
    },

    getAttendance() {      
      this.reportFilters.serviceCodes = this.reportFilters.serviceCodes || '';
      this.reportFilters.startDate = this.reportFilters.startDate || '';
      this.reportFilters.endDate = this.reportFilters.endDate || '';
      this.reportFilters.ClientID = this.reportFilters.ClientID || '';
      this.userState = this.userState || '';

      const params = this.queryParams;
      if (this.clientIds && this.clientIds.length) {
        HttpService.get('/attendance/reports', params)
          .then(response => {
            const {docs, totalPages, totalDocs} = response.data;
            this.attendance = docs.map(attendance => ({
              ...attendance,
              Date: DateTimeUtil.toLocalDate(attendance.Date),
            }));

            this.pageCount = totalPages;
            this.itemCount = totalDocs;
          })
          .catch(error => {
            console.log({error});
          })
          .finally(() => {
            this.isLoading = false
          });
      }
    },

    async downloadAttendance() {
      const validationMessage = this.validateAttendanceDownload();

      if (validationMessage) {
        alert(`Failed to download attendance. ${validationMessage}`);
        return;
      }

      const client = this.clients.find(client => client._id === this.reportFilters.ClientID)

      if (client.State === "Nevada") {
        const response = await this.getClientAttendance({
          clientId: this.reportFilters.ClientID,
          startDate: this.reportFilters.startDate,
          endDate: this.reportFilters.endDate,
        });

        let attendanceData = response.map(item => ({
          date: item.Date,
          staffInitials: this.createInitials(item.Signature.split(" - ")[0]),
          individualInitials: this.createInitials(item.client.Name)
        })).sort((a, b) => new Date(a.date) - new Date(b.date));

        const data = this.flattenArrays({
          attendanceData,
          individualName: response[0].client.Name,
          caseNo: response[0].client.Case,
        })

        return this.download(data)

      }

      window.location.href = `${url}/attendance/download?defaultClientIds=${this.clientIds.toString()}&clientId=${this.reportFilters.ClientID}&serviceCode=${this.reportFilters.ServiceCode}&startDate=${this.reportFilters.startDate}&endDate=${this.reportFilters.endDate}`;
    },

    validateAttendanceDownload() {
      if (!this.reportFilters.startDate) {
        return 'Start date is required.';
      }

      if (!this.reportFilters.endDate) {
        return 'End date is required.';
      }

      if (!this.reportFilters.ClientID) {
        return 'Client is required.';
      }

      // if (!this.clientIds) {
      //     return 'Client ids is required.';
      // }
      //
      // if (!this.ServiceCode) {
      //     return 'Service code is required.';
      // }

      return '';
    },

    getClientID(item) {
      this.client = item.ClientID;
      window.localStorage.setItem('clientId', this.client)
      console.log(this.client)
    },

    initializeServiceCode() {
      if (this.reportFilters.ClientID === 'All') {
        this.isServiceCodeDisabled = true;
        this.reportFilters.ServiceCode = '';
      } else {
        const matchedClient = this.clients.find(client => client._id === this.reportFilters.ClientID);

        if (matchedClient) {
          this.serviceCodes = matchedClient.ServiceCode;
        }
      }
    },

    viewAttendance(item) {
      console.log({item});

      this.$router.push({
        path: `/attendance/${item.ClientID}/update/${item._id}`,
        query: {createAttendance: false}
      })
    },

    async download(data) {

      const result = await this.getJDTAttendanceReportPDF(data);

      let pdfFile =
        "data:application/octet-stream;base64," + result.data.result;

      let fileName = `JDT-Attendance-Log-${Date.now()}.pdf`;

      download(pdfFile, fileName, "application/pdf");
    },

    flattenArrays(data) {
      const flattened = {};

      Object.entries(data).forEach(([key, value]) => {
        if (!Array.isArray(value)) {
          flattened[key] = value;
        } else {
          value.forEach((item, index) => {
            Object.entries(item).forEach(([innerKey, innerValue]) => {
              flattened[`${innerKey}${index + 1}`] = innerValue;
            });
          });
        }
      });

      return flattened;
    },

    createInitials(name) {
      return name
        ? name
          .split(" ")
          .map((staff) => staff[0])
          .join("")
        : "";
    },

    calcUnits() {
      let timeDiff = moment.duration(moment.utc(moment(this.timeEnd, "HH:mm").diff(moment(this.timeStart, "HH:mm"))).format("HH:mm")).asMinutes();
      if (this.newServiceUnit == 'Quarter Hour') {
        this.newUnits = parseInt(timeDiff) / 15
      } else if (this.newServiceUnit == 'Session') {
        this.newUnits = parseInt(timeDiff) / 30
      } else if (this.newServiceUnit == 'Hourly') {
        this.newUnits = parseInt(timeDiff) / 60
      } else {
        this.newUnits = 1
      }
      this.calcForError()

    },

    calcForError() {
      if (moment(this.timeStart, 'hh:mma').isAfter(moment(this.timeEnd, 'hh:mma')) ||
        moment(this.timeStart, 'hh:mma').isSame(moment(this.timeEnd, 'hh:mma'))) {

        this.msg = true;
        this.text = 'Please make sure the end time is after the start time';
        this.newUnits = 0
      }
    },
  }
}
</script>
<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}
</style>

<style>
#print {
  display: none
}

@media only screen and (min-width: 768px) {
  .mobile {
    display: none;
  }
}

@media only screen and (max-width: 600px) {
  .desktop {
    display: none;
  }
}

@media only screen and (min-width: 768px) {
  .mobile {
    display: none;
  }
}

@media only screen and (max-width: 600px) {
  .desktop {
    display: none;
  }
}

@media print {
  #print {
    display: block;
    border: none;
    width: 1200px;
    padding-right: 300px;
    margin-left: -200px;
  }

  nav.v-navigation-drawer, header.v-toolbar, button.v-btn, div.v-card {
    display: none;
    border: none;
  }

  @page {
    margin: 0;
  }

  body {
    margin: 1.6cm;
  }
}
</style>
